import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Dimensions, Metrics, type BreakdownRow } from '@/api/types';
import { BarList } from '@/components/BarList';
import { Callout } from '@/components/Callout';
import { ClarityRecordings } from '@/components/ClarityRecordings';
import { ExportButton } from '@/components/ExportButton';
import { RangeBar } from '@/components/RangeBar';
import { StatTile } from '@/components/StatTile';
import { formatCompact, TrendChart, type TrendSeries } from '@/components/TrendChart';
import { breakdown, useOverview } from '@/hooks/useOverview';
import { AnalyticsLoadError, noSourceSentence } from '@/components/AnalyticsLoadError';

/**
 * The six Microsoft Clarity signals GA4 cannot see. This tab is the main reason to run Clarity
 * at all: GA4 tells you people arrived, Clarity tells you they got stuck.
 */
export function FrustrationPage({ siteKey }: { siteKey: string }) {
  const { data, isLoading, isError, error, days, setDays, from, to, setRange, isCustomRange } = useOverview(siteKey);

  const totals = data?.totals ?? {};
  const hasClarity = data?.providers_included.includes('clarity') ?? false;

  // The Clarity project id, for the recordings deep-link. Shares the cached 'sources' query the
  // shell already runs, so this costs no extra request.
  const { data: sources } = useQuery({ queryKey: ['sources'], queryFn: api.listSources });
  const clarityProjectId = sources?.find(
    (s) => s.provider === 'clarity' && s.site_key === siteKey,
  )?.project_id ?? undefined;

  // Two series, so the two VALIDATED categorical slots. A third was rejected for failing the
  // normal-vision floor — do not add one here.
  const series = useMemo<TrendSeries[]>(() => {
    if (!data) return [];
    const has = (key: string) => data.series.some((p) => p.metrics[key] !== undefined);
    const built: TrendSeries[] = [];

    if (has(Metrics.rageClickCount)) {
      built.push({
        key: 'rage',
        label: 'Rage clicks',
        color: 'var(--series-1)',
        // A day without the metric is a gap, not a calm day.
        points: data.series.map((p) => ({ date: p.date, value: p.metrics[Metrics.rageClickCount] ?? null })),
      });
    }
    if (has(Metrics.deadClickCount)) {
      built.push({
        key: 'dead',
        label: 'Dead clicks',
        color: 'var(--series-2)',
        points: data.series.map((p) => ({ date: p.date, value: p.metrics[Metrics.deadClickCount] ?? null })),
      });
    }
    return built;
  }, [data]);

  const pages = breakdown(data, Dimensions.pagePath);

  return (
    <>
      <RangeBar
        days={days} setDays={setDays} from={from} to={to} data={data}
        setRange={setRange} isCustomRange={isCustomRange}
        actions={
          <ExportButton
            siteKey={siteKey} from={from} to={to}
            dimension={Dimensions.pagePath} label="Export by page"
          />
        }
      />

      {isLoading && <div className="empty-state"><span className="spinner" /></div>}
      {isError && <AnalyticsLoadError error={error} what="frustration signals" notFound={noSourceSentence(siteKey)} />}

      {data && !hasClarity && (
        <Callout tone="info" title="Microsoft Clarity is not connected">
          These signals come only from Clarity — Google Analytics does not measure them. Connect a
          Clarity source on the Sources page.
        </Callout>
      )}

      {data && hasClarity && (
        <>
          {/* Reserved status colours, never a categorical series hue. Each tile pairs the colour
              with a label, so meaning is never carried by colour alone. */}
          <div className="kpi-row">
            <StatTile label="Rage clicks" value={totals[Metrics.rageClickCount] ?? null} tone="critical"
              sub="Repeated clicks in one spot" />
            <StatTile label="Dead clicks" value={totals[Metrics.deadClickCount] ?? null} tone="warning"
              sub="Clicks that did nothing" />
            <StatTile label="Error clicks" value={totals[Metrics.errorClickCount] ?? null} tone="critical"
              sub="Clicks that threw an error" />
            <StatTile label="Excessive scroll" value={totals[Metrics.excessiveScrollCount] ?? null} tone="warning"
              sub="Hunting for content" />
            <StatTile label="Quick backs" value={totals[Metrics.quickbackClickCount] ?? null} tone="warning"
              sub="Opened, then left at once" />
            <StatTile label="Script errors" value={totals[Metrics.scriptErrorCount] ?? null} tone="critical"
              sub="JavaScript failures" />
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="t-heading" style={{ margin: 0 }}>Frustration over time</h2>
              <span className="t-label subtle">Microsoft Clarity</span>
            </div>
            <div className="card-body">
              <TrendChart series={series} missingDates={data.missing_dates} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
            <div className="card">
              <div className="card-header">
                <h2 className="t-heading" style={{ margin: 0 }}>Rage clicks by page</h2>
              </div>
              <div className="card-body">
                <BarList
                  rows={byMetric(pages, Metrics.rageClickCount)}
                  limit={10}
                  emptyLabel="No per-page frustration rows yet."
                />
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h2 className="t-heading" style={{ margin: 0 }}>Dead clicks by page</h2>
              </div>
              <div className="card-body">
                <BarList
                  rows={byMetric(pages, Metrics.deadClickCount)}
                  limit={10}
                  emptyLabel="No per-page frustration rows yet."
                />
              </div>
            </div>
          </div>

          {/* Clarity's recordings are the thing its API cannot serve, so this is a link, not a
              panel that would never fill. */}
          <ClarityRecordings projectId={clarityProjectId} />

          {totals[Metrics.totalBotSessionCount] !== undefined && (
            <Callout tone="info" title="Bot traffic">
              Clarity counted {formatCompact(totals[Metrics.totalBotSessionCount] ?? 0)} bot sessions
              in this range. Bots are excluded from the signals above but explain part of any gap
              between Clarity and Google Analytics session counts.
            </Callout>
          )}
        </>
      )}
    </>
  );
}

/** Rows ranked by ONE frustration metric, dropping pages that never showed it. */
function byMetric(rows: BreakdownRow[], metric: string) {
  return rows
    .map((row) => ({ label: row.value || '(not set)', value: row.metrics[metric] ?? 0 }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
}
